import React, { useCallback, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { Container, Text, useApp } from '@pixi/react';
import { observer } from 'mobx-react-lite';
import { Spine } from '../../../../../core/classes/spine';
import { applicationStore } from '../../../../../store/applicationStore';
import { soundService, SoundTypes } from '../../../../../services/soundService';
import { isoStandard } from '../../../../../core/helpers/isoStandard';
import { gameStore } from '../../../../../store/gameStore';
import { jackPotStore } from '../../../../../store/jackPotStore';
import { gameService } from '../../../../../services/gameService';
import { winBarStore } from '../../../../../store/winBarStore';
import { amountContainerInfo } from '../../../../../core/constants/amountContainerInfo';
import { userInfoStore } from '../../../../../store/userInfoStore';
import { betReplayService } from '../../../../../services/betReplayService';

let timeLine;
const BoostedJackpotWinPopupView = observer(
  ({ setIsWheel, spinWheelWinAmount, setIsBoostedJackpotWinPopup, setSpinWheelWinAmount }) => {
    const { isReplay } = userInfoStore;
    const { setIsShowBlackLayer } = applicationStore;
    const { setJackPotExpireTime } = jackPotStore;

    const [popupWinAmount, setPopupWinAmount] = useState('');
    const [isPopupClick, setIsPopupClick] = useState(true);
    const [isNumberAnimationEnd, setIsNumberAnimationEnd] = useState(false);

    const { stage } = useApp();

    const popupRef = useRef(null);
    const popupTextRef = useRef(null);
    const popupText1Ref = useRef(null);
    const popupWinTextRef = useRef(null);

    useEffect(() => {
      if (popupRef.current) {
        const wheelPopup = new Spine({ key: 'popup' });
        wheelPopup.addChildToSlot('winPopupNumbers', popupText1Ref.current);
        wheelPopup.addChildToSlot('winPopupText', popupTextRef.current);
        popupRef.current.addChild(wheelPopup);
      }
    }, [popupRef]);

    useEffect(() => {
      if (popupRef.current) {
        soundService.play(SoundTypes.wheelWinPopup);
        popupRef.current.children[0].play('start', false);
        popupRef.current.children[0].addListener(
          'complete',
          () => {
            popupRef.current.children[0].play('loop', true);
          },
          null,
          'start'
        );
      }
    }, [setIsBoostedJackpotWinPopup, popupRef]);

    useEffect(() => {
      timeLine && timeLine.kill();
      timeLine = gsap.timeline();
      timeLine.to(popupWinTextRef.current, {
        textContent: spinWheelWinAmount,
        duration: 3,
        ease: 'none',
        snap: { textContent: 0.01 },
        stagger: {
          each: 1,
          onUpdate: function () {
            // eslint-disable-next-line react/no-this-in-sfc
            setPopupWinAmount(isoStandard(this.targets()[0].textContent, true));
          },
          onComplete: function () {
            setPopupWinAmount(isoStandard(+spinWheelWinAmount, true));
            timeLine && timeLine.kill();
            setIsNumberAnimationEnd(true);
            if (isReplay) {
              betReplayService.sendReplayMessage();
            }
          },
        },
      });
    }, [popupWinTextRef, setIsNumberAnimationEnd, setPopupWinAmount, spinWheelWinAmount, isReplay]);

    useEffect(() => {
      return () => {
        gameService.balanceUpdateHandler();
      };
    }, []);

    const handleEndAnimation = useCallback(
      // eslint-disable-next-line consistent-return
      (e) => {
        e.stopPropagation();
        if (!isPopupClick || isReplay) {
          return null;
        }
        if (popupRef.current && isNumberAnimationEnd) {
          setIsPopupClick(false);
          setIsNumberAnimationEnd(false);
          popupRef.current.children[0].play('end', false);
          popupRef.current.children[0].addListener(
            'complete',
            () => {
              soundService.stop(SoundTypes.wheelWinPopup);
              winBarStore.setWinAmount(spinWheelWinAmount);
              winBarStore.updateShowAmountContainerInfo(amountContainerInfo.winContainer, true);
              winBarStore.setLastWinAmount(spinWheelWinAmount);
              setIsShowBlackLayer(false);
              setJackPotExpireTime(300000);
              setIsWheel(false);
              applicationStore.setBonusType(null);
              setIsBoostedJackpotWinPopup(false);
              setSpinWheelWinAmount(0);
              gameStore.setIsSpinStart(false);
            },
            null,
            'end'
          );
        } else {
          setPopupWinAmount(isoStandard(+spinWheelWinAmount, true));
          timeLine && timeLine.kill();
          setIsNumberAnimationEnd(true);
        }
      },
      [
        isReplay,
        popupRef,
        setIsWheel,
        isPopupClick,
        setIsPopupClick,
        spinWheelWinAmount,
        setIsShowBlackLayer,
        setJackPotExpireTime,
        isNumberAnimationEnd,
        setSpinWheelWinAmount,
        setIsNumberAnimationEnd,
        setIsBoostedJackpotWinPopup,
      ]
    );

    const handleKeyDown = useCallback(
      (e) => {
        if (e.code === 'Space' || e.code === 'Enter') {
          e.preventDefault();
          handleEndAnimation(e);
        }
      },
      [handleEndAnimation]
    );

    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    useEffect(() => {
      stage.interactive = true;
      stage.addEventListener('pointerup', handleEndAnimation, true);
      return () => {
        stage.interactive = false;
        stage.removeEventListener('pointerup', handleEndAnimation, true);
      };
    }, [handleEndAnimation, stage]);

    return (
      <>
        <Container ref={popupRef} />
        <Container ref={popupTextRef}>
          <Text
            anchor={0.5}
            text={applicationStore.canvasTranslationKeys.YOU_HAVE_WON}
            style={{
              fontSize: 48,
              fontWeight: 700,
              fontFamily: 'poppinsMedium',
              fill: '#FF9000',
              align: 'center',
              wordWrap: true,
              wordWrapWidth: 500,
            }}
            resolution={2}
          />
        </Container>
        <Container ref={popupText1Ref}>
          <Text
            ref={popupWinTextRef}
            anchor={0.5}
            text={popupWinAmount}
            style={{
              fontSize: 48,
              fontWeight: 700,
              fontFamily: 'poppinsMedium',
              fill: '#0F40CE',
              align: 'center',
              wordWrap: true,
              wordWrapWidth: 500,
            }}
            resolution={2}
          />
        </Container>
      </>
    );
  }
);

export default BoostedJackpotWinPopupView;
